import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  EditIcon, 
  DeleteIcon, 
  SaveIcon, 
  AddIcon, 
  CloseIcon,
  UserIcon,
  AdminIcon,
  DiceIcon,
  ClipboardIcon
} from '../components/Icons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || `${window.location.protocol}//${window.location.hostname}:8000/api`;

function AdminPanel({ token }) {
  const [users, setUsers] = useState([]);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('users');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'user', system_prompt: '' });
  const [newCode, setNewCode] = useState({ code: '', expires_in_hours: 24, system_prompt: '' });
  const [editUser, setEditUser] = useState(null);
  const [editCode, setEditCode] = useState(null);

  const getCsrfToken = () => {
    return document.cookie.split('; ').find(row => row.startsWith('csrf_token='))?.split('=')[1] || '';
  };

  // Copy to clipboard utility
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess('Code copied to clipboard!');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setSuccess('Code copied to clipboard!');
      setTimeout(() => setSuccess(''), 2000);
    }
  };

  // Enhanced access code generation with cryptographically secure algorithms
  const generateRandomCode = () => {
    // Use only secure generation method
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    const crypto = window.crypto || window.msCrypto;
    let generatedCode = '';
    
    if (crypto && crypto.getRandomValues) {
      const array = new Uint32Array(12);
      crypto.getRandomValues(array);
      for (let i = 0; i < 12; i++) {
        generatedCode += chars[array[i] % chars.length];
      }
    } else {
      // High-entropy fallback
      for (let i = 0; i < 12; i++) {
        generatedCode += chars.charAt(Math.floor(Math.random() * chars.length));
      }
    }
    
    setNewCode(c => ({ ...c, code: generatedCode }));
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [usersRes, codesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/admin/users`, { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' }),
        fetch(`${BACKEND_URL}/admin/access-codes`, { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' })
      ]);
      if (!usersRes.ok || !codesRes.ok) throw new Error('Failed');
      setUsers(await usersRes.json());
      setCodes(await codesRes.json());
    } catch (e) {
      setError('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const withHeaders = (extra = {}) => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'X-CSRF-Token': getCsrfToken(),
    ...extra,
  });

  // User CRUD operations
  const handleAddUser = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    try {
      const res = await fetch(`${BACKEND_URL}/admin/users`, {
        method: 'POST',
        headers: withHeaders(),
        credentials: 'include',
        body: JSON.stringify(newUser),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed');
      }
      setSuccess('User added successfully');
      setNewUser({ username: '', password: '', role: 'user', system_prompt: '' });
      fetchAll();
    } catch (e) {
      setError(e.message || 'Failed to add user');
    }
  };

  const handleEditUser = (u) => setEditUser({ ...u });

  const handleUpdateUser = async () => {
    setError(''); setSuccess('');
    try {
      const res = await fetch(`${BACKEND_URL}/admin/users/${editUser.username}`, {
        method: 'PATCH',
        headers: withHeaders(),
        credentials: 'include',
        body: JSON.stringify(editUser),
      });
      if (!res.ok) throw new Error();
      setSuccess('User updated successfully');
      setEditUser(null);
      fetchAll();
    } catch (e) {
      setError(e.message || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    setError(''); setSuccess('');
    try {
      const res = await fetch(`${BACKEND_URL}/admin/users/${username}`, {
        method: 'DELETE',
        headers: withHeaders(),
        credentials: 'include',
      });
      if (!res.ok) throw new Error();
      setSuccess('User deleted successfully');
      fetchAll();
    } catch (e) {
      setError(e.message || 'Failed to delete user');
    }
  };

  // Access Code CRUD operations
  const handleAddCode = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    try {
      const res = await fetch(`${BACKEND_URL}/admin/access-codes`, {
        method: 'POST',
        headers: withHeaders(),
        credentials: 'include',
        body: JSON.stringify(newCode),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed');
      }
      
      // Copy the new code to clipboard
      await copyToClipboard(newCode.code);
      
      setNewCode({ code: '', expires_in_hours: 24, system_prompt: '' });
      fetchAll();
    } catch (e) {
      setError(e.message || 'Failed to add access code');
    }
  };

  const handleEditCode = (c) => setEditCode({ ...c });

  const handleUpdateCode = async () => {
    setError(''); setSuccess('');
    try {
      const res = await fetch(`${BACKEND_URL}/admin/access-codes/${editCode.code}`, {
        method: 'PATCH',
        headers: withHeaders(),
        credentials: 'include',
        body: JSON.stringify(editCode),
      });
      if (!res.ok) throw new Error();
      setSuccess('Access code updated successfully');
      setEditCode(null);
      fetchAll();
    } catch (e) {
      setError(e.message || 'Failed to update access code');
    }
  };

  const handleDeleteCode = async (code) => {
    if (!window.confirm('Are you sure you want to delete this access code?')) return;
    setError(''); setSuccess('');
    try {
      const res = await fetch(`${BACKEND_URL}/admin/access-codes/${code}`, {
        method: 'DELETE',
        headers: withHeaders(),
        credentials: 'include',
      });
      if (!res.ok) throw new Error();
      setSuccess('Access code deleted successfully');
      fetchAll();
    } catch (e) {
      setError(e.message || 'Failed to delete access code');
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Admin Panel</h1>
        <p>Manage users and access codes</p>
      </div>

      <div className="admin-tabs">
        <button 
          className={`tab-button ${tab === 'users' ? 'active' : ''}`} 
          onClick={() => setTab('users')}
        >
          <UserIcon size={18} />
          Users
        </button>
        <button 
          className={`tab-button ${tab === 'codes' ? 'active' : ''}`} 
          onClick={() => setTab('codes')}
        >
          <AdminIcon size={18} />
          Access Codes
        </button>
      </div>

      {error && <div className="alert error">{error}</div>}
      {success && <div className="alert success">{success}</div>}

      {loading ? (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      ) : (
        <div className="admin-content">
          {tab === 'users' ? (
            <div className="admin-section">
              {/* Add User Form */}
              <div className="form-card">
                <h3>Add New User</h3>
                <form onSubmit={handleAddUser} className="admin-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Username</label>
                      <input 
                        type="text" 
                        placeholder="Enter username" 
                        value={newUser.username} 
                        onChange={e => setNewUser(u => ({ ...u, username: e.target.value }))} 
                        required 
                      />
                    </div>
                    <div className="form-group">
                      <label>Password</label>
                      <input 
                        type="password" 
                        placeholder="Enter password" 
                        value={newUser.password} 
                        onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))} 
                        required 
                      />
                    </div>
                    <div className="form-group">
                      <label>Role</label>
                      <select 
                        value={newUser.role} 
                        onChange={e => setNewUser(u => ({ ...u, role: e.target.value }))}
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>System Prompt (Optional)</label>
                    <textarea 
                      placeholder="Enter system prompt" 
                      value={newUser.system_prompt} 
                      onChange={e => setNewUser(u => ({ ...u, system_prompt: e.target.value }))}
                      rows={3}
                    />
                  </div>
                  <button type="submit" className="btn primary">
                    <AddIcon size={16} />
                    Add User
                  </button>
                </form>
              </div>

              {/* Users Table */}
              <div className="table-card">
                <h3>Users ({users.length})</h3>
                <div className="table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Username</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>System Prompt</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(u => (
                        <React.Fragment key={u.username}>
                          <tr className={editUser && editUser.username === u.username ? 'editing' : ''}>
                            <td>
                              <div className="user-cell">
                                <UserIcon size={16} />
                                {u.username}
                              </div>
                            </td>
                            <td>
                              {editUser && editUser.username === u.username ? (
                                <select 
                                  value={editUser.role} 
                                  onChange={e => setEditUser(eu => ({ ...eu, role: e.target.value }))}
                                  className="inline-select"
                                >
                                  <option value="user">User</option>
                                  <option value="admin">Admin</option>
                                </select>
                              ) : (
                                <span className={`role-badge ${u.role}`}>{u.role}</span>
                              )}
                            </td>
                            <td>
                              {editUser && editUser.username === u.username ? (
                                <select 
                                  value={editUser.is_active ? 'active' : 'inactive'} 
                                  onChange={e => setEditUser(eu => ({ ...eu, is_active: e.target.value === 'active' }))}
                                  className="inline-select"
                                >
                                  <option value="active">Active</option>
                                  <option value="inactive">Inactive</option>
                                </select>
                              ) : (
                                <span className={`status-badge ${u.is_active ? 'active' : 'inactive'}`}>
                                  {u.is_active ? 'Active' : 'Inactive'}
                                </span>
                              )}
                            </td>
                            <td>
                              <div className="system-prompt">
                                {u.system_prompt || <em>None</em>}
                              </div>
                            </td>
                            <td>
                              <div className="action-buttons">
                                {editUser && editUser.username === u.username ? (
                                  <>
                                    <button onClick={handleUpdateUser} className="btn success small">
                                      <SaveIcon size={14} />
                                    </button>
                                    <button onClick={() => setEditUser(null)} className="btn secondary small">
                                      <CloseIcon size={14} />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button onClick={() => handleEditUser(u)} className="btn secondary small">
                                      <EditIcon size={14} />
                                    </button>
                                    <button onClick={() => handleDeleteUser(u.username)} className="btn danger small">
                                      <DeleteIcon size={14} />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                          {editUser && editUser.username === u.username && (
                            <tr className="edit-row">
                              <td colSpan="5">
                                <div className="edit-system-prompt">
                                  <label>System Prompt:</label>
                                  <textarea 
                                    value={editUser.system_prompt || ''} 
                                    onChange={e => setEditUser(eu => ({ ...eu, system_prompt: e.target.value }))}
                                    placeholder="Enter system prompt..."
                                    rows={4}
                                  />
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="admin-section">
              {/* Add Code Form */}
              <div className="form-card">
                <h3>Add New Access Code</h3>
                <form onSubmit={handleAddCode} className="admin-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Access Code</label>
                      <div className="input-with-button">
                        <input 
                          type="text" 
                          placeholder="Enter access code" 
                          value={newCode.code} 
                          onChange={e => setNewCode(c => ({ ...c, code: e.target.value }))} 
                          required 
                        />
                        <button 
                          type="button" 
                          onClick={generateRandomCode}
                          className="btn secondary dice-btn"
                          title="Generate secure code"
                        >
                          <DiceIcon size={20} />
                        </button>
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Expires In (Hours)</label>
                      <input 
                        type="number" 
                        placeholder="24" 
                        value={newCode.expires_in_hours} 
                        onChange={e => setNewCode(c => ({ ...c, expires_in_hours: Number(e.target.value) }))} 
                        min={1}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>System Prompt (Optional)</label>
                    <textarea 
                      placeholder="Enter system prompt" 
                      value={newCode.system_prompt} 
                      onChange={e => setNewCode(c => ({ ...c, system_prompt: e.target.value }))}
                      rows={3}
                    />
                  </div>
                  <button type="submit" className="btn primary">
                    <AddIcon size={16} />
                    Add Code
                  </button>
                </form>
              </div>

              {/* Codes Table */}
              <div className="table-card">
                <h3>Access Codes ({codes.length})</h3>
                <div className="table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Expires At</th>
                        <th>Status</th>
                        <th>System Prompt</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {codes.map(c => (
                        <React.Fragment key={c.code}>
                          <tr className={editCode && editCode.code === c.code ? 'editing' : ''}>
                            <td>
                              <div className="code-with-copy">
                                <button 
                                  onClick={() => copyToClipboard(c.code)}
                                  className="copy-btn"
                                  title="Copy to clipboard"
                                >
                                  <ClipboardIcon size={16} />
                                </button>
                                <code className="code-cell">{c.code}</code>
                              </div>
                            </td>
                            <td>{new Date(c.expires_at).toLocaleString()}</td>
                            <td>
                              {editCode && editCode.code === c.code ? (
                                <select 
                                  value={editCode.is_active ? 'active' : 'inactive'} 
                                  onChange={e => setEditCode(ec => ({ ...ec, is_active: e.target.value === 'active' }))}
                                  className="inline-select"
                                >
                                  <option value="active">Active</option>
                                  <option value="inactive">Inactive</option>
                                </select>
                              ) : (
                                <span className={`status-badge ${c.is_active ? 'active' : 'inactive'}`}>
                                  {c.is_active ? 'Active' : 'Inactive'}
                                </span>
                              )}
                            </td>
                            <td>
                              <div className="system-prompt">
                                {c.system_prompt || <em>None</em>}
                              </div>
                            </td>
                            <td>
                              <div className="action-buttons">
                                {editCode && editCode.code === c.code ? (
                                  <>
                                    <button onClick={handleUpdateCode} className="btn success small">
                                      <SaveIcon size={14} />
                                    </button>
                                    <button onClick={() => setEditCode(null)} className="btn secondary small">
                                      <CloseIcon size={14} />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button onClick={() => handleEditCode(c)} className="btn secondary small">
                                      <EditIcon size={14} />
                                    </button>
                                    <button onClick={() => handleDeleteCode(c.code)} className="btn danger small">
                                      <DeleteIcon size={14} />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                          {editCode && editCode.code === c.code && (
                            <tr className="edit-row">
                              <td colSpan="5">
                                <div className="edit-system-prompt">
                                  <label>System Prompt:</label>
                                  <textarea 
                                    value={editCode.system_prompt || ''} 
                                    onChange={e => setEditCode(ec => ({ ...ec, system_prompt: e.target.value }))}
                                    placeholder="Enter system prompt..."
                                    rows={4}
                                  />
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth || auth.role !== 'admin') {
      navigate('/');
    }
  }, [auth, navigate]);

  if (!auth || auth.role !== 'admin') {
    return null;
  }

  return (
    <div className="admin-page">
      <div className="admin-nav">
        <button 
          className="back-button"
          onClick={() => navigate('/')}
        >
          ← Back to Chat
        </button>
        <div className="nav-user">
          <UserIcon size={18} />
          <span>{auth.username}</span>
          <button onClick={logout} className="logout-btn">
            Sign Out
          </button>
        </div>
      </div>
      <AdminPanel token={auth.access_token} />
    </div>
  );
} 